// File: frontend/src/components/marketing/motion.js
//
// Small hooks and Framer Motion variants used INSIDE the marketing chart
// visuals (components/marketing/visuals). Section-level motion on the public
// pages is GSAP, in landingMotion.js; the two never animate the same element.
// Everything here is reduced-motion aware: animations resolve instantly to
// their final state and content is never hidden.

import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "framer-motion";

const EASE = [0.22, 1, 0.36, 1];

export const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: { opacity: 1, y: 0, transition: { duration: 0.55, ease: EASE } },
};

export const staggerContainer = (stagger = 0.08, delayChildren = 0) => ({
  hidden: {},
  show: { transition: { staggerChildren: stagger, delayChildren } },
});

// Count-up hook that respects reduced motion (jumps straight to target) and only
// runs once when `active` becomes true. Cleans up its rAF.
export function useCountUp(target, { duration = 1100, active = true } = {}) {
  const reduce = useReducedMotion();
  const [value, setValue] = useState(reduce ? target : 0);
  const rafRef = useRef(null);
  const startedRef = useRef(false);

  useEffect(() => {
    if (reduce) { setValue(target); return; }
    if (!active || startedRef.current) return;
    startedRef.current = true;
    let startTs = null;
    const from = 0;
    const step = (ts) => {
      if (startTs == null) startTs = ts;
      const p = Math.min((ts - startTs) / duration, 1);
      const eased = 1 - Math.pow(1 - p, 3);
      setValue(from + (target - from) * eased);
      if (p < 1) rafRef.current = requestAnimationFrame(step);
    };
    rafRef.current = requestAnimationFrame(step);
    return () => { if (rafRef.current) cancelAnimationFrame(rafRef.current); };
  }, [target, duration, active, reduce]);

  return value;
}

// True once the element has scrolled into view (IntersectionObserver). Used to
// gate expensive reveals/count-ups until visible. Disconnects after first hit.
export function useInViewOnce(options = { threshold: 0.3 }) {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el || inView) return;
    if (typeof IntersectionObserver === "undefined") { setInView(true); return; }
    const obs = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { setInView(true); obs.disconnect(); } });
    }, options);
    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return [ref, inView];
}

// Whether the element is on screen right now (IntersectionObserver), for
// looping decoration that should stop once scrolled past. Framer Motion's
// useInView assumes IntersectionObserver exists; this treats a browser without
// it as "always on screen" instead of throwing.
export function useOnScreen(ref, rootMargin = "100px") {
  const [onScreen, setOnScreen] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;
    if (typeof IntersectionObserver === "undefined") { setOnScreen(true); return undefined; }
    const obs = new IntersectionObserver(([entry]) => setOnScreen(entry.isIntersecting), { rootMargin });
    obs.observe(el);
    return () => obs.disconnect();
  }, [ref, rootMargin]);
  return onScreen;
}
